import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Button } from '@packages/utils';
import { EIconNames } from 'components/Base/Icon';
import {
  EWorkshopFilterParams,
  EWorkshopSortParams,
  TWorkshopDataUI,
  WORKSHOP_NO_ELC_FILTER_VALUE,
} from 'data/types/workshops.types';
import { Table } from 'components/Base/Table';
import {
  useGetWorkshopCitiesQuery,
  useGetWorkshopHostsQuery,
  useGetWorkshopLocationQuery,
  useGetWorkshopsQuery,
} from 'redux/apiSlice/workshops.slice';
import { useGetElcIdentitiesQuery } from 'redux/apiSlice/elcs.slice';
import useTable from 'components/Base/Table/hooks/useTable';
import CardContent from 'components/Base/Layout/Content/CardContent';
import PageWrapper from 'components/Base/PageWrapper';
import { ETableStoragePaths } from 'components/Base/Table/Table.types';
import { TBackButtonState } from 'components/Base/PageWrapper/PageWrapper.types';
import { TRtkErrorType, TTableFilterDataUI } from 'data/types/generic.types';
import { ETagType } from 'data/types/tags.types';
import { useGetTagsListQuery } from 'redux/apiSlice/tags.slice';
import { useCheckPermission } from 'hooks/useCheckPermission';
import useDidUpdate from 'hooks/useDidUpdate';
import useSessionDateSort from 'components/Shared/SessionDateSort/useSessionDateSort';
import { DEFAULT_DATE_SORT_COLUMN } from 'components/Shared/SessionDateSort/sessionDateSort.helpers';
import CreateWorkshopModal from '../CreateWorkshop';
import useGetColumnsData from './WorkshopList.data';
import SessionDates from './components/SessionDates';
import WorkshopExport from './components/WorkshopExport';

const WorkshopList = () => {
  const { defaultColumns } = useGetColumnsData();
  const {
    page,
    pageSize,
    sortColumn,
    setPage,
    setPageSize,
    setSortColumn,
    handleTableChange,
    selectedFilters,
    hasSelectedFilter,
    hasFilters,
    columns,
    setColumns,
    onFiltersReset,
    selectedRows,
    setSelectedRows,
  } = useTable<TWorkshopDataUI>({
    storagePath: ETableStoragePaths.WORKSHOPS,
    defaultColumns,
    // Next session date ascending on load and after Reset Filters (GROW-4270)
    defaultSortColumn: DEFAULT_DATE_SORT_COLUMN,
  });

  // The Session Date column is a regular server-side sorter on the backend's
  // next session date (PART_DATE), sent as sortField + sortDirection.
  const { isDateSorted, onTableChange } = useSessionDateSort<TWorkshopDataUI>({
    sortColumn,
    setSortColumn,
    setPage,
    handleTableChange,
  });

  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.workshops.list',
  });

  const navigate = useNavigate();
  const [openCreateWorkshopModal, setOpenCreateWorkshopModal] =
    useState<boolean>(false);

  const { data, isFetching, isLoading, error } = useGetWorkshopsQuery({
    page,
    pageSize,
    sortColumn,
    filters: selectedFilters,
  });

  const { data: citiesFilters } = useGetWorkshopCitiesQuery();
  const { data: locationFilters } = useGetWorkshopLocationQuery();

  const { data: hostsFilters } = useGetWorkshopHostsQuery();

  const { data: tagsFilters } = useGetTagsListQuery(ETagType.WORKSHOP);

  const { data: elcIdentities } = useGetElcIdentitiesQuery();

  // Prepend a "Not in an ELC" sentinel to the ELC filter options; the
  // workshopFilterConverter splits it back into `includeNoElc: true`.
  const elcFilters = useMemo<TTableFilterDataUI[] | undefined>(() => {
    if (!elcIdentities) return undefined;
    return [
      {
        text: t('table.NOT_IN_ELC'),
        value: WORKSHOP_NO_ELC_FILTER_VALUE,
      },
      ...elcIdentities.map(elc => ({ text: elc.label, value: elc.value })),
    ];
  }, [elcIdentities, t]);

  useEffect(() => {
    setColumns(
      columns.map(item => {
        if (item.key === EWorkshopFilterParams.HOST) {
          item.filters = hostsFilters;
        }
        if (item.key === EWorkshopFilterParams.CITY) {
          item.filters = citiesFilters;
        }
        if (item.key === EWorkshopFilterParams.LOCATION) {
          item.filters = locationFilters;
        }
        if (item.key === EWorkshopFilterParams.TAGS) {
          item.filters = tagsFilters;
        }
        if (item.key === EWorkshopFilterParams.ELC) {
          item.filters = elcFilters;
        }
        return item;
      }),
    );
  }, [citiesFilters, hostsFilters, tagsFilters, locationFilters, elcFilters]);

  useEffect(() => {
    setColumns(
      columns.map(item => {
        if (item.key === EWorkshopSortParams.NEXT_SESSION_DATE) {
          // While sorted by date, mark each row's next session using the
          // backend's nextSessionPart flag (GROW-4274).
          item.render = (
            sessionDates: TWorkshopDataUI['sessionDates'],
            record: TWorkshopDataUI,
          ) => (
            <SessionDates
              sessionDates={sessionDates}
              nextSessionPart={record.nextSessionPart}
              highlightNextSession={isDateSorted}
            />
          );
        }
        return item;
      }),
    );
  }, [isDateSorted]);

  const navigateToWorkshopDetails = (record: TWorkshopDataUI) => {
    return {
      onClick: () => {
        navigate(record.workshopId, {
          state: {
            backUrl: window.location.pathname,
            backButtonSuffix: t('TITLE'),
          } as TBackButtonState,
        });
      },
    };
  };
  const isEmpty = !data?.content.length && !hasSelectedFilter && !isFetching;

  // A new filter can hide selected rows; start the selection over so
  // Export Selected never sends rows that are no longer in the list. Keyed on
  // the applied filter values because every table change (paging, sorting)
  // hands over a new filters object, with a null for each unfiltered column.
  const selectedFiltersKey = JSON.stringify(
    Object.entries(selectedFilters)
      .filter(([, value]) => value?.length)
      .sort(([a], [b]) => a.localeCompare(b)),
  );
  useDidUpdate(() => {
    setSelectedRows({ selectedRowsKeys: [], selectedRowsRecords: [] });
  }, [selectedFiltersKey]);

  const headerActions = useMemo(
    () => [
      <WorkshopExport
        key="export-actions"
        selectedRowKeys={selectedRows.selectedRowsKeys}
        selectedFilters={selectedFilters}
        sortColumn={sortColumn}
        columnKeys={columns.map(column => String(column.key))}
        disabled={!data?.content?.length}
      />,
    ],
    [selectedRows, selectedFilters, sortColumn, columns, data],
  );

  const canAddWorkshop = useCheckPermission('WORKSHOP_ADD');

  return (
    <>
      <PageWrapper
        title={t('TITLE')}
        {...(canAddWorkshop && {
          actions: (
            <Button
              onClick={() => setOpenCreateWorkshopModal(true)}
              color="primary"
              type="primary"
            >
              {t('actions.create-workshop.CREATE_WORKSHOP_BTN')}
            </Button>
          ),
        })}
        isLoading={isLoading}
        error={error as TRtkErrorType}
        emptyWrapper={{
          isEmpty,
          alt: 'Handbag',
          icon: EIconNames.HANDBAG,
          ...(canAddWorkshop && { btnText: t('empty-view.BTN_TEXT') }),
          emptyText: t('empty-view.EMPTY_TEXT'),
          onClick: () => setOpenCreateWorkshopModal(true),
        }}
      >
        <CardContent>
          <Table<TWorkshopDataUI>
            onFiltersReset={onFiltersReset}
            hasFilters={hasFilters}
            rowKey={(record: TWorkshopDataUI) => record.workshopId}
            currentPage={page}
            columns={columns}
            dataSource={data?.content}
            rowClassName="pointer"
            onRow={navigateToWorkshopDetails}
            loading={isFetching}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalDataCount={data?.totalElements}
            onChange={onTableChange}
            storagePath={ETableStoragePaths.WORKSHOPS}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            headerActions={headerActions}
          />
        </CardContent>
      </PageWrapper>
      {openCreateWorkshopModal && (
        <CreateWorkshopModal
          open={openCreateWorkshopModal}
          setOpen={setOpenCreateWorkshopModal}
        />
      )}
    </>
  );
};

export default WorkshopList;
